import {
  createSlider,
  getObserver,
  debounce,
  lockScroll,
  unlockScroll,
  SkyLazyMedia,
  closeDrawer,
  lockLenis,
  unlockLenis,
  setDrawerOpenAccessibility,
} from '@theme/theme-global';

/** Cached dynamic import — only load PhotoSwipe when zoom opens. */
let photoswipeLightboxPromise = null;

function loadPhotoswipeLightbox() {
  if (!photoswipeLightboxPromise) {
    photoswipeLightboxPromise = import('@lib/photoswipe-lightbox').then((mod) => mod.default);
  }
  return photoswipeLightboxPromise;
}

if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.shopify-section');
      this.sliderInstance = null;
      this.thumbsCarouselInstance = null;
      this.mobileSliderInstance = null;
      this.thumbsMobileInstance = null;
      this._observer = null;
      this.thumbs = null;
      this.thumbsCarousel = null;
      this.thumbsMobile = null;
      this.mobileSliderEl = null;
      this._thumbSpace = null;
      this._mediaGalleryHeight = null;
      this.updateMediaGalleryHeight = this.updateMediaGalleryHeight.bind(this);
      this._lgMq = window.matchMedia('(min-width: 1025px)');
      this._onLgChange = this._onLgChange.bind(this);
      this._onResize = debounce(() => {
        this.updateMediaGalleryHeight();
        this.syncAutoplayForViewport();
        if (this.mobileSliderInstance) {
          this.mobileSliderInstance.update?.();
          this.thumbsMobileInstance?.update?.();
        }
      }, 150);
    }

    connectedCallback() {
      this.thumbs = this.querySelector('[data-thumbnails="true"]');
      this.thumbsCarousel = this.querySelector('[is="media-gallery-thumbs-carousel"]');
      this.thumbsMobile = this.querySelector('[is="media-gallery-thumbs-mobile"]');
      this.mobileSliderEl = this.querySelector('.media-gallery-mobile [data-mobile-params]');

      if (!this.sliderInstance) this.init();
      this.scheduleMobileGallery();
      this.bindSkipToProductInfo();
      window.addEventListener('resize', this._onResize, { passive: true });
      this._lgMq.addEventListener('change', this._onLgChange);
      requestAnimationFrame(() => {
        this.updateMediaGalleryHeight();
        this.syncAutoplayForViewport();
      });
      this.addEventListener('lightbox:open', (e) => {
        const galleryIndex = Number.isInteger(e.detail?.index) ? e.detail.index : 0;
        this.openZoom(galleryIndex);
      });
    }

    bindSkipToProductInfo() {
      const skipLink = this.closest('.product-media-wrapper')?.querySelector('[data-skip-to-product-info]');
      if (!skipLink || skipLink.dataset.boundSkip === 'true') return;

      skipLink.dataset.boundSkip = 'true';
      this._onSkipToProductInfo = (event) => {
        const href = skipLink.getAttribute('href');
        if (!href || href.charAt(0) !== '#') return;

        const target = document.getElementById(href.slice(1));
        if (!target) return;

        event.preventDefault();
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: false });
        if (typeof target.scrollIntoView === 'function') {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      };

      skipLink.addEventListener('click', this._onSkipToProductInfo);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      this._lgMq.removeEventListener('change', this._onLgChange);
      this.destroySlider();
      this.sliderInstance = null;
      this._mediaGalleryHeight = null;
    }

    _onLgChange(event) {
      if (event.matches) {
        if (!this.sliderInstance) this.init();
        this.updateMediaGalleryHeight();
      } else {
        this.scheduleMobileGallery();
      }
      this.syncAutoplayForViewport();
    }

    ensureMobileGallery() {
      if (this._lgMq.matches) return;
      this.initMobileGallery();
    }

    scheduleMobileGallery() {
      if (this._lgMq.matches) return;
      if (this.mobileSliderInstance || this._mobileSliderInitializing) return;
      if (!this.mobileSliderEl?.dataset.mobileParams) return;

      const slideCount = this.mobileSliderEl.querySelectorAll('.swiper-slide').length;
      if (slideCount < 2) return;

      this.ensureMobileGallery();
    }

    async initMobileGallery() {
      if (this.mobileSliderInstance || this._mobileSliderInitializing) return;
      if (!this.mobileSliderEl?.dataset.mobileParams) return;

      this._mobileSliderInitializing = true;
      try {
        const wrapper = this.mobileSliderEl.querySelector('.swiper-wrapper');
        const scrolled = wrapper ? wrapper.scrollLeft : 0;
        const startIndex = scrolled > 0
          ? Math.round(scrolled / (this.mobileSliderEl.clientWidth || 1))
          : 0;

        if (this.thumbsMobile && !this.thumbsMobileInstance) {
          this.thumbsMobileInstance = await createSlider(this.thumbsMobile, {
            ...JSON.parse(this.thumbsMobile.dataset.params || '{}'),
            bypassLcpGate: true,
          });
        }

        const mobileParams = JSON.parse(this.mobileSliderEl.dataset.mobileParams);
        mobileParams.bypassLcpGate = true;
        if (startIndex > 0) mobileParams.initialSlide = startIndex;
        if (this.thumbsMobileInstance) {
          mobileParams.thumbs = { swiper: this.thumbsMobileInstance };
        }

        this.mobileSliderInstance = await createSlider(this.mobileSliderEl, mobileParams);

        this.mobileSliderInstance.on('slideChangeTransitionEnd', () => {
          this.handleActiveSlideMedia(this.mobileSliderInstance);
        });
        this.handleActiveSlideMedia(this.mobileSliderInstance);
      } finally {
        this._mobileSliderInitializing = false;
      }
    }

    init() {
      if (!this._lgMq.matches) return;
      const isFirstSection = this.section && this.section.querySelector('[data-index]')?.dataset.index === '1';
      const sliderParamsEl = this.querySelector('[data-params]');
      if (!sliderParamsEl?.dataset.params) return;

      const setupSliders = async () => {
        if (this.sliderInstance || this._sliderInitializing) return;
        this._sliderInitializing = true;

        try {
          // Desktop carousel thumbs — native Swiper thumbs module only (no manual slideToLoop)
          if (this.thumbsCarousel && !this.thumbsCarouselInstance) {
            this.thumbsCarouselInstance = await createSlider(
              this.thumbsCarousel,
              JSON.parse(this.thumbsCarousel.dataset.params || '{}'),
            );
          }

          const sliderParams = JSON.parse(sliderParamsEl.dataset.params);
          if (this.thumbsCarouselInstance) {
            sliderParams.thumbs = { swiper: this.thumbsCarouselInstance };
          }

          this.sliderInstance = await createSlider(sliderParamsEl, sliderParams);

          this.sliderInstance.on('slideChangeTransitionEnd', () => this.handleActiveSlideMedia());
          this.handleActiveSlideMedia();
          this.updateMediaGalleryHeight();
        } finally {
          this._sliderInitializing = false;
        }
      };

      if (isFirstSection) {
        setupSliders();
      } else if ('IntersectionObserver' in window) {
        this._observer = getObserver({ rootMargin: '300px 0px', threshold: 0 }, true, false);
        this._observer.observe(this, () => {
          if (!this.sliderInstance) setupSliders();
        });
      } else {
        setupSliders();
      }
    }

    isVisible(el) {
      if (!el) return false;
      const desktop = this._lgMq.matches;
      if (el.classList.contains('media-gallery-mobile') || el.closest('.media-gallery-mobile')) {
        return !desktop;
      }
      if (el.classList.contains('media-gallery-desktop') || el.closest('.media-gallery-desktop')) {
        return desktop;
      }
      return true;
    }

    updateMediaGalleryHeight() {
      if (!this.classList.contains('left-vertical-filled')) return;
      if (!this._lgMq.matches) return;

      const heightEl = this.querySelector('[data-get-height="true"]');
      const height = heightEl?.offsetHeight || this.offsetHeight;
      if (!(height > 0)) return;

      if (this._thumbSpace == null) {
        this._thumbSpace =
          this.querySelector('[data-get-style-property="true"]')?.style.getPropertyValue('--productThumbSpace') ||
          '0px';
      }

      if (height === this._mediaGalleryHeight) return;
      this._mediaGalleryHeight = height;
      this.style.setProperty(
        '--mediaGalleryHeight',
        `calc(${height}px - ${this._thumbSpace} - ${this._thumbSpace})`,
      );
    }

    destroySlider() {
      if (this.sliderInstance?.destroy) {
        this.sliderInstance.destroy(true, true);
      }

      if (this.thumbsCarouselInstance?.destroy) {
        this.thumbsCarouselInstance.destroy(true, true);
      }

      if (this.mobileSliderInstance?.destroy) {
        this.mobileSliderInstance.destroy(true, true);
      }

      if (this.thumbsMobileInstance?.destroy) {
        this.thumbsMobileInstance.destroy(true, true);
      }

      if (this._observer) {
        this._observer.unobserve(this);
        this._observer = null;
      }

      this.sliderInstance = null;
      this.thumbsCarouselInstance = null;
      this.mobileSliderInstance = null;
      this.thumbsMobileInstance = null;
    }

    handleActiveSlideMedia(swiper = this.sliderInstance) {
      if (!swiper) return;

      // Mobile + desktop galleries both exist in the DOM. Only autoplay the
      // gallery that is actually visible for the current viewport.
      const galleryRoot =
        swiper.el?.closest('.media-gallery-mobile, .media-gallery-desktop') || swiper.el;
      if (!this.isVisible(galleryRoot)) {
        galleryRoot
          ?.querySelectorAll('sky-lazy-media')
          .forEach((el) => el.pauseMedia?.());
        return;
      }

      SkyLazyMedia.pauseAll();

      const activeSlide = swiper.el.querySelector('.swiper-slide-active');
      const activeMedia = activeSlide?.querySelectorAll('sky-lazy-media[data-slide-aware="true"]') || [];

      activeMedia.forEach((mediaEl) => {
        if (mediaEl.dataset.autoplay === 'true') {
          mediaEl._playTrigger = 'autoplay';
          mediaEl.loadAndPlayMedia();
        }
      });
    }

    // Pause hidden gallery media and resume autoplay in the visible one
    // (e.g. after resize crosses the mobile/desktop breakpoint).
    syncAutoplayForViewport() {
      if (this.dataset.enableVideoAutoplay !== 'true') return;

      const mobile = this.querySelector('.media-gallery-mobile');
      const desktop = this.querySelector('.media-gallery-desktop');

      if (mobile && !this.isVisible(mobile)) {
        mobile.querySelectorAll('sky-lazy-media').forEach((el) => el.pauseMedia?.());
      }
      if (desktop && !this.isVisible(desktop)) {
        desktop.querySelectorAll('sky-lazy-media').forEach((el) => el.pauseMedia?.());
      }

      if (mobile && this.isVisible(mobile) && this.mobileSliderInstance) {
        this.handleActiveSlideMedia(this.mobileSliderInstance);
        return;
      }

      if (desktop && this.isVisible(desktop) && this.sliderInstance) {
        this.handleActiveSlideMedia(this.sliderInstance);
        return;
      }

      this.playStandaloneAutoplayMedia();
    }

    // Single-media carousel falls back to a non-Swiper grid, so slide-change
    // handlers never run — kick off autoplay videos in the visible gallery.
    playStandaloneAutoplayMedia() {
      if (this.dataset.enableVideoAutoplay !== 'true') return;
      if (this.sliderInstance || this.mobileSliderInstance) return;

      const root = this.getActiveGalleryRoot();
      if (!root || !this.isVisible(root)) return;

      root.querySelectorAll('sky-lazy-media[data-autoplay="true"]').forEach((mediaEl) => {
        if (mediaEl.dataset.slideAware === 'true') return;
        mediaEl._playTrigger = 'autoplay';
        mediaEl.loadAndPlayMedia?.();
      });
    }

    getSlideGalleryIndex(slide) {
      const fromData = parseInt(slide?.getAttribute('data-index'), 10);
      return Number.isInteger(fromData) ? fromData : null;
    }

    getSlideByGalleryIndex(galleryIndex, root = this.getActiveGalleryRoot()) {
      if (!Number.isInteger(galleryIndex)) return null;
      const baseSlides = this.getBaseSlides(root);
      return (
        baseSlides.find((slide) => this.getSlideGalleryIndex(slide) === galleryIndex) ||
        baseSlides[galleryIndex] ||
        null
      );
    }

    syncGalleryToIndex(galleryIndex) {
      if (!Number.isInteger(galleryIndex) || galleryIndex < 0) return;

      const slideTo = (instance) => {
        if (!instance) return;
        if (typeof instance.slideToLoop === 'function') {
          instance.slideToLoop(galleryIndex);
          return;
        }
        if (typeof instance.slideTo === 'function') {
          instance.slideTo(galleryIndex);
        }
      };

      slideTo(this.sliderInstance);
      slideTo(this.mobileSliderInstance);
    }

    buildPhotoswipeImageItems(baseSlides = this.getBaseSlides(this.getActiveGalleryRoot())) {
      const items = [];

      baseSlides.forEach((slide, orderedIndex) => {
        if (slide.dataset.mediaType !== 'image') return;

        const img = slide.querySelector('img.main-product-media, img');
        if (!img) return;

        const galleryIndex = this.getSlideGalleryIndex(slide);
        const resolvedGalleryIndex = Number.isInteger(galleryIndex) ? galleryIndex : orderedIndex;

        const w = parseInt(
          img.dataset.pswpW ||
          img.getAttribute('width') ||
          img.naturalWidth ||
          1600,
          10
        );

        const h = parseInt(
          img.dataset.pswpH ||
          img.getAttribute('height') ||
          img.naturalHeight ||
          1600,
          10
        );

        items.push({
          width: w,
          height: h,
          src: img.currentSrc || img.src,
          srcset: img.getAttribute('srcset') || '',
          msrc: img.currentSrc || img.src,
          alt: img.alt || '',
          thumbnailElement: img,
          thumbCropped: true,
          galleryIndex: resolvedGalleryIndex,
        });
      });

      return items;
    }

    getPhotoswipeIndexForGalleryIndex(items, galleryIndex) {
      if (!items?.length) return 0;

      if (Number.isInteger(galleryIndex)) {
        const exact = items.findIndex((item) => item.galleryIndex === galleryIndex);
        if (exact >= 0) return exact;

        // Click landed on non-image (or stale index): nearest previous image, else next.
        let nearest = -1;
        for (let i = 0; i < items.length; i += 1) {
          if (items[i].galleryIndex <= galleryIndex) nearest = i;
          else break;
        }
        if (nearest >= 0) return nearest;
      }

      return 0;
    }

    async getPhotoswipe() {
      if (this._photoswipe) return this._photoswipe;
      if (this._photoswipeInitPromise) return this._photoswipeInitPromise;

      this._photoswipeInitPromise = (async () => {
        const PhotoSwipeLightbox = await loadPhotoswipeLightbox();

        const lightbox = new PhotoSwipeLightbox({
          bgOpacity: 1,
          pswpModule: () => import('@lib/photoswipe-core'),
          allowPanToNext: false,
          allowMouseDrag: true,
          wheelToZoom: false,
          // Manual restore in close handler (desktop only). Built-in returnFocus
          // alone can miss when opener is gone / Swiper moved after next-prev.
          returnFocus: false,
          zoom: false,
          arrowPrevSVG:
            '<svg class="icon icon-navigation-arrow-left icon-small" viewBox="0 0 10 10" fill="none"> <path d="M8.33594 5.17773H2.05794" stroke="currentColor" stroke-width="1" stroke-linecap="round"/> <path d="M5.05078 1.66797L1.56078 5.15797L5.05078 8.37997" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/> </svg>',
          arrowNextSVG:
            '<svg class="icon icon-navigation-arrow-right icon-small" viewBox="0 0 10 10" fill="none"> <path d="M1.66406 5.17773H7.94206" stroke="currentColor" stroke-width="1" stroke-linecap="round"/> <path d="M4.94922 1.66797L8.43922 5.15797L4.94922 8.37997" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/> </svg>',
          closeSVG:
            '<svg class="icon icon-close icon-small" fill="none" viewBox="0 0 24 24" > <path d="M18.75 5.13496L5.25 18.8544" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path> <path d="M18.75 18.8544L5.25 5.13496" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path> </svg>',
        });

        lightbox.addFilter('thumbEl', (thumbEl, data) => {
          if (data?.thumbnailElement) return data.thumbnailElement;

          const slide = this.getSlideByGalleryIndex(data?.galleryIndex);
          const img = slide?.querySelector('img.main-product-media, img');
          return img || thumbEl;
        });

        // Keep Swiper on the matching gallery slide (not PhotoSwipe's image-only index)
        lightbox.on('change', () => {
          const data = lightbox.pswp?.currSlide?.data;
          const galleryIndex = Number.isInteger(data?.galleryIndex)
            ? data.galleryIndex
            : null;

          if (Number.isInteger(galleryIndex)) {
            this.syncGalleryToIndex(galleryIndex);

            const slide = this.getSlideByGalleryIndex(galleryIndex);
            const img = slide?.querySelector('img.main-product-media, img');
            if (img && data) {
              data.thumbnailElement = img;
              data.thumbCropped = true;
            }
          }
        });

        lightbox.on('close', () => {
          const data = lightbox.pswp?.currSlide?.data;
          const galleryIndex = Number.isInteger(data?.galleryIndex)
            ? data.galleryIndex
            : null;

          if (Number.isInteger(galleryIndex)) {
            this.syncGalleryToIndex(galleryIndex);
          }

          unlockScroll?.();
          this.restoreLightboxFocus(galleryIndex);
        });

        lightbox.init();
        this._photoswipe = lightbox;
        return this._photoswipe;
      })();

      try {
        return await this._photoswipeInitPromise;
      } finally {
        this._photoswipeInitPromise = null;
      }
    }

    isDesktopGalleryActive() {
      const desktop = this.querySelector('.media-gallery-desktop');
      return !!(desktop && this.isVisible(desktop));
    }

    getLightboxZoomButton(galleryIndex) {
      const slide = this.getSlideByGalleryIndex(galleryIndex);
      return slide?.querySelector('.media-zoom-btn, [is="media-lightbox-button"]') || null;
    }

    restoreLightboxFocus(galleryIndex) {
      // Keyboard focus restore is desktop-only; mobile zoom stays tabindex="-1".
      if (!this.isDesktopGalleryActive()) {
        this._lightboxOpener = null;
        return;
      }

      const opener = this._lightboxOpener;
      this._lightboxOpener = null;

      const zoomBtn = this.getLightboxZoomButton(galleryIndex);
      // Prefer the zoom control for the image that was visible on close
      // (after next/prev), then fall back to the original opener.
      const target =
        zoomBtn ||
        (opener && document.contains(opener) && this.contains(opener) && opener);

      if (!target || typeof target.focus !== 'function') return;

      // Defer past PhotoSwipe teardown so focus is not lost to browser chrome.
      requestAnimationFrame(() => {
        try {
          target.focus({ preventScroll: true });
        } catch (_) { }
      });
    }

    async openZoom(startGalleryIndex = 0) {
      const baseSlides = this.getBaseSlides(this.getActiveGalleryRoot());
      const items = this.buildPhotoswipeImageItems(baseSlides);

      if (!items.length) return;

      const pswpIndex = this.getPhotoswipeIndexForGalleryIndex(items, startGalleryIndex);
      const active = document.activeElement;
      this._lightboxOpener =
        active && this.contains(active) ? active : this.getLightboxZoomButton(startGalleryIndex);

      const lightbox = await this.getPhotoswipe();
      lightbox.loadAndOpen(pswpIndex, items);
      lockScroll?.();
    }

    getActiveGalleryRoot() {
      const mobile = this.querySelector('.media-gallery-mobile');
      const desktop = this.querySelector('.media-gallery-desktop');
      if (mobile && this.isVisible(mobile)) return mobile;
      if (desktop && this.isVisible(desktop)) return desktop;
      return desktop || mobile || this;
    }

    getBaseSlides(root) {
      const all = Array.from(root.querySelectorAll('[data-media-item]'));
      // Pick exactly one slide per data-index, ordered by that index
      const bucket = new Map();
      for (const el of all) {
        const k = parseInt(el.getAttribute('data-index'), 10);
        if (!Number.isNaN(k) && !bucket.has(k)) bucket.set(k, el);
      }
      return Array.from(bucket.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([, el]) => el);
    }

    parseRatio(str) {
      if (!str) return 1;
      const s = String(str).trim();
      if (s.includes('/')) {
        const [w, h] = s.split('/').map(Number);
        return w > 0 && h > 0 ? w / h : 1;
      }
      const n = parseFloat(s);
      return isFinite(n) && n > 0 ? n : 1;
    }
  }
  customElements.define('media-gallery', MediaGallery);
}

if (!customElements.get('media-lightbox-button')) {
  class MediaLightboxButton extends HTMLButtonElement {
    constructor() {
      super();
      this.addEventListener('click', () => {
        const slideEl = this.closest('[data-media-item]');
        const idxFromData = slideEl ? parseInt(slideEl.getAttribute('data-index'), 10) : null;
        const swIdx = (this.closest('[data-media-item]') || slideEl)?.getAttribute('data-swiper-slide-index');

        this.dispatchEvent(
          new CustomEvent('lightbox:open', {
            bubbles: true,
            detail: {
              index: Number.isInteger(idxFromData) ? idxFromData : null, // ← clicked logical index
              swiperIndex: swIdx != null ? parseInt(swIdx, 10) : null, // optional fallback
            },
          }),
        );
      });
    }
  }
  customElements.define('media-lightbox-button', MediaLightboxButton, { extends: 'button' });
}

if (!customElements.get('media-gallery-grid-thumbs')) {
  class MediaGalleryGridThumbs extends HTMLDivElement {
    constructor() {
      super();
      this.activeIndex = 0;
      this.onNavClick = this.onNavClick.bind(this);
      this.onThumbClick = this.onThumbClick.bind(this);
      this.updateSlider = this.updateSlider.bind(this);
      this._slideMetrics = null;
      this._onResize = debounce(() => {
        this._slideMetrics = null;
        this.updateSlider();
      }, 150);
      this._releaseScrollSync = this._releaseScrollSync.bind(this);
      this._onMediaVisibilityChange = this._onMediaVisibilityChange.bind(this);
      this._syncActiveFromScroll = this._syncActiveFromScroll.bind(this);
    }

    connectedCallback() {
      this.init();
    }

    get thumbnailsRoot() {
      return this.closest('[data-thumbnails="true"]');
    }

    get wrapper() {
      return this.querySelector('[data-media-gallery-thumbs-wrapper="true"]');
    }

    get thumbs() {
      return this.wrapper ? Array.from(this.wrapper.querySelectorAll('[data-thumbnail-item="true"]')) : [];
    }

    get mediaGallery() {
      return this.closest('media-gallery');
    }

    onScrollHandleThumbs() {
      const gallery = this.mediaGallery;
      if (!gallery || this._scrollSyncReady) return;

      const mediaItems = gallery.querySelectorAll('[data-media-item="true"]');
      if (!mediaItems.length || !this.thumbs.length) return;

      this._thumbIndexByMediaId = new Map(
        this.thumbs.map((thumb, index) => [thumb.getAttribute('data-media-id'), index]),
      );
      this._visibleMedia = new Map();
      this._observedMediaItems = Array.from(mediaItems);
      this._scrollObserver = getObserver({ threshold: [0, 0.25, 0.5, 0.75, 1] }, false, true);

      this._observedMediaItems.forEach((item) => {
        this._scrollObserver.observe(item, this._onMediaVisibilityChange);
      });
      this._scrollSyncReady = true;
    }

    _onMediaVisibilityChange(entry) {
      if (this._suppressScrollSync) return;

      const id = entry.target.getAttribute('data-media-id');
      if (entry.isIntersecting) {
        this._visibleMedia.set(id, entry.target);
      } else {
        this._visibleMedia.delete(id);
      }

      if (!this._scrollRaf) {
        this._scrollRaf = requestAnimationFrame(() => {
          this._scrollRaf = 0;
          this._syncActiveFromScroll();
        });
      }
    }

    _syncActiveFromScroll() {
      if (this._suppressScrollSync || !this._visibleMedia?.size) return;

      const viewportCenter = window.innerHeight / 2;
      let bestId = null;
      let bestDistance = Infinity;

      for (const [id, el] of this._visibleMedia) {
        const rect = el.getBoundingClientRect();
        const distance = Math.abs(rect.top + rect.height / 2 - viewportCenter);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestId = id;
        }
      }

      if (!bestId) return;

      const index = this._thumbIndexByMediaId.get(bestId);
      if (index == null || index === this.activeIndex) return;

      this.setActiveIndex(index, { scrollToMedia: false });
    }

    _releaseScrollSync() {
      this._suppressScrollSync = false;
    }

    _lockScrollSync() {
      this._suppressScrollSync = true;
      window.addEventListener('scrollend', this._releaseScrollSync, { once: true });
      clearTimeout(this._scrollSyncTimer);
      this._scrollSyncTimer = setTimeout(this._releaseScrollSync, 1200);
    }

    scrollToMediaItem(target) {
      if (!target) return;

      this._lockScrollSync();

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const scrollOptions = { block: 'center', inline: 'nearest' };

      if (reducedMotion) {
        target.scrollIntoView({ behavior: 'auto', ...scrollOptions });
        return;
      }

      if (window.lenis?.scrollTo) {
        const rect = target.getBoundingClientRect();
        const viewportHalf = window.innerHeight / 2;
        const elementCenter = rect.top + rect.height / 2;
        const targetScrollY = window.scrollY + elementCenter - viewportHalf;
        window.lenis.scrollTo(Math.max(0, targetScrollY), { duration: 0.8, immediate: false });
        return;
      }

      target.scrollIntoView({ behavior: 'smooth', ...scrollOptions });
    }

    getSlideMetrics() {
      if (this._slideMetrics) return this._slideMetrics;

      const thumb = this.thumbs[0];
      if (!thumb || !this.wrapper) return { slideStep: 0, slidesPerView: 1 };

      const thumbHeight = thumb.offsetHeight;
      const clientHeight = this.clientHeight;
      if (!thumbHeight || !clientHeight) return { slideStep: 0, slidesPerView: 1 };

      const styles = getComputedStyle(this.wrapper);
      const gap = parseFloat(styles.rowGap || styles.gap) || 6;
      const slideStep = thumbHeight + gap;
      const slidesPerView = Math.max(1, Math.floor((clientHeight + gap) / slideStep));

      this._slideMetrics = { slideStep, slidesPerView };
      return this._slideMetrics;
    }

    getTranslateY(activeIndex) {
      const { slideStep, slidesPerView } = this.getSlideMetrics();
      const total = this.thumbs.length;

      if (!slideStep || total <= slidesPerView) return 0;

      const maxOffsetIndex = total - slidesPerView;
      const offsetIndex = Math.max(0, Math.min(activeIndex - slidesPerView + 1, maxOffsetIndex));

      return offsetIndex * slideStep;
    }

    setActiveIndex(index, { scrollToMedia = true } = {}) {
      const total = this.thumbs.length;
      if (!total) return;

      this.activeIndex = ((index % total) + total) % total;
      this.updateSlider();

      if (!scrollToMedia) return;

      const activeThumb = this.thumbs[this.activeIndex];
      const mediaId = activeThumb?.getAttribute('data-media-id');
      const target = this.closest('media-gallery')?.querySelector(`[data-media-item][data-media-id="${mediaId}"]`);
      this.scrollToMediaItem(target);
    }

    updateSlider() {
      const thumbs = this.thumbs;
      if (!this.wrapper || !thumbs.length) return;

      thumbs.forEach((thumb, index) => {
        const isActive = index === this.activeIndex;
        thumb.classList.toggle('swiper-slide-active', isActive);
        thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      const translateY = this.getTranslateY(this.activeIndex);
      this.wrapper.style.transform = `translate3d(0, -${translateY}px, 0)`;
    }

    onNavClick(event) {
      const action = event.currentTarget?.getAttribute('data-action');
      if (!action) return;

      if (action === 'next-slide') {
        this.setActiveIndex(this.activeIndex + 1);
        return;
      }

      if (action === 'previous-slide') {
        this.setActiveIndex(this.activeIndex - 1);
      }
    }

    onThumbClick(event) {
      const thumb = event.target.closest('.product-thumbnail[data-media-id]');
      if (!thumb) return;

      const index = this.thumbs.indexOf(thumb);
      if (index === -1) return;

      this.setActiveIndex(index);
    }

    init() {
      const root = this.thumbnailsRoot;
      if (!root || !this.wrapper) return;

      const initialIndex = this.thumbs.findIndex((thumb) => thumb.getAttribute('aria-current') === 'true');
      this.activeIndex = initialIndex >= 0 ? initialIndex : 0;

      if (!this._navBound) {
        root.querySelectorAll('[data-action="previous-slide"], [data-action="next-slide"]').forEach((button) => {
          button.addEventListener('click', this.onNavClick);
        });
        this._navBound = true;
      }

      if (!this._thumbClickBound) {
        this.addEventListener('click', this.onThumbClick);
        this._thumbClickBound = true;
      }

      if (!this._resizeBound) {
        window.addEventListener('resize', this._onResize, { passive: true });
        this._resizeBound = true;
      }

      this.updateSlider();
      this.onScrollHandleThumbs();
    }

    disconnectedCallback() {
      const root = this.thumbnailsRoot;

      if (this._scrollObserver) {
        this._observedMediaItems?.forEach((item) => {
          this._scrollObserver.unobserve(item);
        });
        this._scrollObserver = null;
        this._observedMediaItems = null;
      }

      if (this._scrollRaf) {
        cancelAnimationFrame(this._scrollRaf);
        this._scrollRaf = 0;
      }

      clearTimeout(this._scrollSyncTimer);
      window.removeEventListener('scrollend', this._releaseScrollSync);
      this._scrollSyncReady = false;
      this._visibleMedia = null;
      this._thumbIndexByMediaId = null;

      if (this._navBound && root) {
        root.querySelectorAll('[data-action="previous-slide"], [data-action="next-slide"]').forEach((button) => {
          button.removeEventListener('click', this.onNavClick);
        });
        this._navBound = false;
      }

      if (this._thumbClickBound) {
        this.removeEventListener('click', this.onThumbClick);
        this._thumbClickBound = false;
      }

      if (this._resizeBound) {
        window.removeEventListener('resize', this._onResize);
        this._resizeBound = false;
      }

      this._slideMetrics = null;
    }
  }
  customElements.define('media-gallery-grid-thumbs', MediaGalleryGridThumbs, { extends: 'div' });
}
/* Retry mobile autoplay after the gallery finishes loading. */
(() => {
  const initialize = () => {
    if (!window.matchMedia('(max-width: 1024px)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const gallery = document.querySelector('media-gallery');
    if (!gallery) return;

    let interacted = false;

    for (const event of ['pointerdown', 'touchstart', 'keydown']) {
      gallery.addEventListener(event, () => {
        interacted = true;
      }, { once: true, passive: true });
    }

    const attempt = () => {
      if (interacted || document.hidden) return;

      const media = gallery.querySelector(
        '.media-gallery-mobile .swiper-slide-active sky-lazy-media[data-autoplay="true"]'
      );

      if (!media || typeof media.loadContent !== 'function') return;

      const rect = media.getBoundingClientRect();
      const visibleHeight =
        Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);

      if (!rect.height || visibleHeight < rect.height * 0.3) return;

      media.loadContent();

      const video = media.querySelector('video');
      if (!video || !video.paused) return;

      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.controls = false;
video.removeAttribute('controls');

      media._playTrigger = 'autoplay';
      media.loadAndPlayMedia();
    };

    [0, 300, 1000].forEach(delay => {
      window.setTimeout(attempt, delay);
    });
  };

  if (document.readyState === 'complete') {
    initialize();
  } else {
    window.addEventListener('load', initialize, { once: true });
  }
})();